import { Tooltip } from '@mui/material';
import tooltipContent from './Timeline/TooltipContent';
import React, { FC, ReactElement, memo, useState } from 'react';
import { isEqual } from 'underscore';
import { dispatch_theme } from '@/config/colors';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AppDispatch, RootState } from '../../index';
import { Badge } from '@mui/material';
import { ItemRendererGetResizePropsReturnType, ReactCalendarItemRendererProps } from 'react-calendar-timeline';
import { TimelineItemCustom } from '../../config/types/Routes';
import { setMustModeShipments } from '@/actions/routes/routesPure';
import DetailShipmentForm from './Timeline/DetailShipmentForm';
import { TimelineIndicator } from './Timeline/ADR';

export enum ItemType {
  START = 'start',
  END = 'end',
  MAIN = 'main',
  REASSIGNED = 'reassigned_item'
}

export const ItemTemplate: FC<ReactCalendarItemRendererProps<TimelineItemCustom>> = memo((props: ReactCalendarItemRendererProps<TimelineItemCustom>): ReactElement => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStop, setDialogStop] = useState<any>(null);
  const [dialogDelivery, setDialogDelivery] = useState<any>(null);
  const { item, itemContext, getItemProps, getResizeProps } = props;
  const { allShipments, delivery, isReassigned, isUpdatePlanningFlag, stop } = item;
  const dispatch: AppDispatch = useDispatch();
  const { left: leftResizeProps, right: rightResizeProps }: ItemRendererGetResizePropsReturnType = getResizeProps();
  const itemProps = getItemProps(item.itemProps);

  const isConnector = item?.itemProps?.type === 'connector';
  if (isConnector) {
    const { style, ...otherProps } = itemProps;
    return (
      <div {...otherProps} style={{ position: 'relative', ...style, ...item.itemProps.style }}>
        {/* dashed horizontal bar on top */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            borderTop: '2px dashed #918e8e',
            height: 0,
          }}
        />
        {/* left vertical leg (touches P box edge) */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 0,
            borderLeft: '3px solid #918e8e',
          }}
        />
        {/* right vertical leg (touches D box edge) */}
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 0,
            borderRight: '3px solid #918e8e',
          }}
        />
      </div>
    );
  }

  const { mustModeShipments, isMustModeEnabled } = useSelector(
    (s: RootState) => ({
      mustModeShipments: s.routes.mustModeShipments,
      isMustModeEnabled: s.routes.isMustModeEnabled,
    }),
    shallowEqual
  );

  const isPickup = (stop?.shipments_to_load?.length ?? 0) > 0;
  const isDelivery = (stop?.shipments_to_unload?.length ?? 0) > 0;
  const isMultistop = isPickup && isDelivery;
  const isPath = item?.itemProps?.type === 'path';

  // this function is for Multistop border
  const getBorder = () => {
    const res: any = { border: 'inherit' };
    if (isMultistop) {
      res.border = `2px solid transparent`;
      res.borderImage = `linear-gradient(90deg, #0288d1,  #ef5350) 1`;
    }
    // If the item is reassigned, apply a red dotted border
    if (isReassigned) {
      res.border = '2px dotted #ef5927';
    }
    return res;
  };

  const getBackground = () => {
    if (mustModeShipments?.includes(delivery?.id ?? delivery?.multi)) {
      return 'repeating-linear-gradient(165deg, #ccc, #ccc 2px, #ef5927 2px, #ef5927 4px)';
    }

    return `${delivery?.status_color ? '#' + delivery?.status_color : '#e5e5e5'}`;
  };

  if (item?.itemProps?.type === 'main') {
    itemProps.style = {
      ...itemProps.style,
      background: getBackground(),
      ...getBorder()
    };
  }

  if ((isPickup || isDelivery) && !isMultistop) {
    itemProps.style = {
      ...itemProps.style,
      borderBottom: isDelivery
        ? `3px solid ${dispatch_theme.palette.info.main}`
        : isPickup
          ? `3px solid ${dispatch_theme.palette.error.light}`
          : 'none',
      ...getBorder(),
    };
  }

  if (isPath) {
    const { style, ...otherProps } = itemProps;
    return (
      <div {...otherProps} style={{ ...style, ...item.itemProps.style }}>
        {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : ''}
        <div
          className="rct-item-content"
          style={{ maxHeight: `${itemContext.dimensions.height}` }}
        >
          {itemContext.title}
        </div>

        {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : ''}
      </div>
    );
  }

  if (item.itemProps.type === 'start' || item.itemProps.type === 'end') {
    if (item.itemProps.type === 'start') {
      itemProps.style.borderTopLeftRadius = 3;
      itemProps.style.borderBottomLeftRadius = 3;
      itemProps.style.borderBottom = 'none';
    }
    if (item.itemProps.type === 'end') {
      itemProps.style.borderTopRightRadius = 3;
      itemProps.style.borderBottomRightRadius = 3;
    }
    return (<div {...itemProps}>
      {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : ''}
      <div
        className="rct-item-content"
        style={{ maxHeight: `${itemContext.dimensions.height}` }}
      >
        {itemContext.title}
      </div>

      {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : ''}
    </div>);
  }

  const assignMustRelationshipToItem = () => {
    const mustShipments = mustModeShipments?.includes(delivery?.id ?? delivery?.multi) ?
      mustModeShipments.filter((shipmentId: number | string) => shipmentId != (delivery.id ?? delivery?.multi)) : [...mustModeShipments, delivery?.id ?? delivery?.multi];
    dispatch(setMustModeShipments(mustShipments));
  };

  const handleDoubleClick = () => {
    setDialogStop(stop);
    setDialogDelivery(allShipments);
    setDialogOpen(true);
  };

  return (
    <>
      <Tooltip title={tooltipContent({ stop, delivery })}>
        <div {...itemProps}
          id={`${item?.itemProps?.type}-${delivery?.custom_id}`}
          onDoubleClick={handleDoubleClick}
          {...(isUpdatePlanningFlag && isMustModeEnabled ? { onClick: assignMustRelationshipToItem } : {})}>
          {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : ''}
          {/* This is the timeline item content with badges */}
          <div
            className="rct-item-content"
            style={{
              maxHeight: `${itemContext.dimensions.height}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              position: 'relative',
              top: '-7px',
              left: '2px'
            }}>
            {(() => {
              const loadCount = stop?.shipments_to_load?.length ?? 0
              const unloadCount = stop?.shipments_to_unload?.length ?? 0;
              const total = loadCount + unloadCount;

              if (total <= 1) return null;

              // Both pickup & delivery → show SUM badge (neutral color)
              if (loadCount > 0 && unloadCount > 0) {
                return (
                  <Badge
                    badgeContent={total}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: 'transparent',
                        backgroundImage: 'linear-gradient(135deg, #0288d1, #ef5350)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: '11px',
                        minWidth: '0',
                        fontWeight: 600,
                      },
                    }}
                  />
                );
              }

              // Only pickups (and >1 because total>1)
              if (loadCount > 0) {
                return (
                  <Badge
                    badgeContent={loadCount}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{
                      '& .MuiBadge-badge': {
                        color: dispatch_theme.palette.error.light,
                        fontSize: '10px',
                        minWidth: '0',
                        backgroundColor: 'transparent',
                      },
                    }}
                  />
                );
              }

              // Only deliveries (and >1 because total>1)
              if (unloadCount > 0) {
                return (
                  <Badge
                    badgeContent={unloadCount}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{
                      '& .MuiBadge-badge': {
                        color: dispatch_theme.palette.info.main,
                        fontSize: '10px',
                        minWidth: '0',
                        backgroundColor: 'transparent',
                      },
                    }}
                  />
                );
              }
              return null;
            })()}
            <span style={{ marginLeft: 6 }}>{itemContext.title}</span>
          </div>
          <TimelineIndicator shipments={allShipments} />
          {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : ''}
        </div>
      </Tooltip>
      {/* Dialog */}
      <DetailShipmentForm
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        stop={dialogStop}
        shipments={dialogDelivery}
      />
    </>
  );
}, isEqual);

ItemTemplate.displayName = 'ItemTemplate';
